import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[var(--button-gap)] whitespace-nowrap font-[var(--button-font-weight)] tracking-[-0.02em] transition-all duration-150 ease-out cursor-pointer active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      shape: {
        // Pill is the app baseline; `rounded` is the 12px modal-form action
        // (Noor-CPanel merchant dialog: Cancel/Save). Geometry is dialed from
        // component-sizing.css (`--button-radius-pill` / `--button-radius`).
        pill: 'rounded-[var(--button-radius-pill)]',
        rounded: 'rounded-[var(--button-radius)]',
      },
      variant: {
        default:
          'bg-button-primary-bg text-button-primary-fg shadow-sm hover:bg-button-primary-bg-hover hover:shadow-md active:bg-button-primary-bg-active',
        destructive:
          'bg-button-destructive-bg text-button-destructive-fg shadow-sm hover:bg-button-destructive-bg-hover hover:shadow-md active:bg-button-destructive-bg-active',
        outline:
          'border border-button-outline-border bg-button-outline-bg text-button-outline-fg shadow-xs hover:bg-button-outline-bg-hover hover:text-button-outline-fg-hover hover:border-ring/40 hover:shadow-sm',
        secondary:
          'bg-button-secondary-bg text-button-secondary-fg shadow-xs hover:bg-button-secondary-bg-hover hover:shadow-sm',
        ghost:
          'text-button-ghost-fg hover:bg-button-ghost-bg-hover hover:text-button-ghost-fg-hover active:translate-y-0',
        link: 'text-button-link-fg underline-offset-4 hover:underline active:translate-y-0',
        cancel:
          'border border-button-cancel-border bg-button-cancel-bg text-button-cancel-fg shadow-xs hover:bg-button-cancel-bg-hover hover:shadow-sm',
        // Noor-CPanel merchant dialog actions (Figma 1067:23605): `dark` is the
        // navy save, `soft` the flat light-gray cancel/archive/delete.
        dark: 'bg-button-dark-bg text-button-dark-fg shadow-sm hover:bg-button-dark-bg-hover hover:shadow-md active:bg-button-dark-bg-active',
        soft: 'bg-button-soft-bg text-button-soft-fg shadow-xs hover:bg-button-soft-bg-hover hover:shadow-sm',
      },
      /* Pill geometry matches the Figma primary button (rounded-[99px], tall).
       * The default is a 48px-tall pill with 16px text — the app's baseline;
       * `md` is the 44px modal-form action (Cancel/Save), `modal` the 40px
       * 14px-text Noor-CPanel merchant dialog action (Archive/Cancel/Save).
       * Heights, paddings and type all resolve through `--button-*` sizing
       * handles (component-sizing.css); the icon-compensation paddings stay
       * literal — they are optical corrections, not design knobs. */
      size: {
        default:
          'h-[var(--button-height)] px-[var(--button-padding-x)] text-[length:var(--button-font-size)] has-[>svg]:px-5',
        md: 'h-[var(--button-height-md)] px-[var(--button-padding-x-md)] text-[length:var(--button-font-size)] has-[>svg]:px-4',
        modal:
          'h-[var(--button-height-modal)] px-[var(--button-padding-x-modal)] text-[length:var(--button-font-size-modal)] has-[>svg]:px-4',
        sm: 'h-[var(--button-height-sm)] gap-[var(--button-gap-sm)] px-[var(--button-padding-x-sm)] text-[length:var(--button-font-size-sm)] has-[>svg]:px-3.5',
        lg: 'h-[var(--button-height-lg)] px-[var(--button-padding-x-lg)] text-[length:var(--button-font-size)] has-[>svg]:px-6',
        icon: 'h-[var(--button-icon-size)] w-[var(--button-icon-size)]',
      },
    },
    defaultVariants: {
      shape: 'pill',
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  shape,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ shape, variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
